import {
  ComponentProps,
  ElementType,
  ReactNode,
  forwardRef,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import { PortalContainerContext } from '@spotify-internal/encore-web';

import {
  getReactAriaTourLayer,
  subscribeReactAriaTourLayer,
} from '../reactAriaTourLayerRegistry';
import { Molecule } from './Molecule';
import { DialogContext, useDialogContext } from './context';
import { useContentContext } from './useContentContext';
import { DialogSubmitHandler, useDialogForm } from './useDialogForm';

import styles from './DialogComposite.module.css';

export type ContentProps = {
  isLoading?: boolean;
  component?: ElementType;
  className?: string;
  children?: ReactNode;
  'aria-label'?: string;
  onSubmit?: DialogSubmitHandler;
} & Omit<
  ComponentProps<ElementType>,
  'className' | 'children' | 'classes' | 'onSubmit' | 'ref'
>;

export const Content = forwardRef<HTMLElement, ContentProps>((props, ref) => {
  const context = useDialogContext();
  const {
    id,
    open,
    variant = 'default',
    onClose,
    isLoading,
    isFullScreen,
  } = context;

  const {
    component: Component = 'div',
    className,
    isLoading: contentIsLoading,
    onSubmit,
    children,
    ...rest
  } = props;

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const [topLayerContainer, setTopLayerContainer] =
    useState<HTMLElement | null>(null);
  const tourLayer = useSyncExternalStore(
    subscribeReactAriaTourLayer,
    getReactAriaTourLayer,
    getReactAriaTourLayer,
  );
  const [isTourDropdownActive, setIsTourDropdownActive] = useState(false);

  useEffect(() => {
    if (!tourLayer) {
      setIsTourDropdownActive(false);
      return undefined;
    }

    return tourLayer.subscribeDropdownActive(active => {
      setIsTourDropdownActive(current =>
        current === active ? current : active,
      );
    });
  }, [tourLayer]);

  const [{ loading, error }, handleSubmit] = useDialogForm(onSubmit, onClose);
  const value = useContentContext(context, {
    isLoading: loading || isLoading || contentIsLoading,
    error,
  });
  const dialogId: string = rest.id ?? id;
  const dialogDescriptionId: string | undefined =
    rest['aria-describedby'] ??
    (['confirmation', 'alert'].includes(variant)
      ? `${dialogId}-body`
      : undefined);

  const content = (
    <DialogContext.Provider value={value}>
      <Dialog
        aria-label={rest['aria-label']}
        aria-labelledby={rest['aria-labelledby'] ?? `${dialogId}-title`}
        aria-describedby={dialogDescriptionId}
        className={classNames(styles.modal, {
          [styles.modalFullScreen]: isFullScreen,
        })}
       data-track-source-anchor="757d2b30be;content-dialog">
        <Molecule
          id={id}
          as={Component}
          className={className}
          onSubmit={Component === 'form' ? handleSubmit : undefined}
          data-track-source-anchor="a9675954a0;content-molecule" {...rest}
          ref={ref}
          variant={variant}
          onClose={onClose}
          role={undefined}
          aria-labelledby={undefined}
          aria-describedby={undefined}
        >
          {children}
        </Molecule>
      </Dialog>
    </DialogContext.Provider>
  );

  const usesTopLayer = open && isTourDropdownActive;
  const overlayPortalContainer =
    (usesTopLayer ? topLayerContainer : null) ?? portalContainer ?? undefined;

  return (
    <>
      <ModalOverlay
        isOpen={open}
        onOpenChange={isOpen => {
          if (!isOpen) onClose();
        }}
        isDismissable
        shouldCloseOnInteractOutside={target =>
          getReactAriaTourLayer()?.shouldCloseOnInteractOutside(target) ?? true
        }
        data-testid="dialog-overlay"
        className={classNames(styles.overlay, {
          [styles.overlayFullScreen]: isFullScreen,
        })}
      >
        <Modal
          ref={setPortalContainer}
          className={classNames(styles.modal, {
            [styles.modalFullScreen]: isFullScreen,
          })}
        >
          <PortalContainerContext.Provider value={overlayPortalContainer}>
            {content}
          </PortalContainerContext.Provider>
        </Modal>
      </ModalOverlay>
      {usesTopLayer
        ? createPortal(
            <div
              ref={setTopLayerContainer}
              data-react-aria-top-layer
              data-testid="dialog-top-layer"
            />,
            document.body,
          )
        : null}
    </>
  );
});

Content.displayName = 'DialogComposite.Content';

export default Content;
